import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  canInsertStationRouteWaypoint,
  CourseFormFields,
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  createErrorResult,
  editFormDataAF,
  EditFormMode,
  errors,
  generateStationRouteWaypointId,
  ICourseDto,
  ICourseMetadata,
  ICourseStationInfo,
  insertStationRouteWaypoint,
  IStationRouteWaypointLocationDraft,
  RebuildRouteTypeEnum,
  resolveStationRouteWaypointAreaId,
  resolveStationRouteWaypointInsertAfterIndex,
  resolveStationRouteWaypointName,
  SimpleResult,
  toWaypointMapStations,
  useSystemParams,
  WaypointCategoryEnum,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { useCourseDtoActions } from "./use-course-dto-actions";

export const useCourseWaypointActions = () => {
  const { t } = useTranslation();
  const { doNotAutoUpdateCourses } = useSystemParams();
  const { mode } = useCrudEditContext<ICourseDto>();
  const [courseDto] = useRecoilState<Partial<ICourseDto> | undefined>(
    editFormDataAF(["course", mode || EditFormMode.CrudRoute]),
  );
  const { updateEditingCourseMetadata, applyEditingCourseMetadataLocally } =
    useCourseDtoActions();
  const { setValue } = useFormContext<ICourseDto>();
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );

  const stations = useMemo(
    () => toWaypointMapStations(courseDto?.metadata?.stationsInfo ?? []),
    [courseDto?.metadata?.stationsInfo],
  );

  const addWaypointFromDraft = useEvent(
    async (
      draft: IStationRouteWaypointLocationDraft,
    ): Promise<SimpleResult<void>> => {
      const courseStations = courseDto?.metadata?.stationsInfo ?? [];

      if (
        !courseStations.length ||
        !courseDto?.metadata ||
        !canInsertStationRouteWaypoint(courseStations)
      ) {
        return createErrorResult(
          errors.common.technicalProblemOccurred,
          "Cannot add waypoint",
        );
      }

      const insertAfterIndex =
        draft.insertAfterIndex ??
        resolveStationRouteWaypointInsertAfterIndex({
          stations: courseStations,
          latitude: draft.latitude,
          longitude: draft.longitude,
          routePolyline: courseDto.metadata[CourseMetadataKeys.routePolyline],
        });

      const neighborStation = courseStations[insertAfterIndex];
      const areaId = await resolveStationRouteWaypointAreaId({
        latitude: draft.latitude,
        longitude: draft.longitude,
        stations: courseStations,
        insertAfterIndex,
      });
      const waypoint: ICourseStationInfo = {
        id: generateStationRouteWaypointId(),
        name: resolveStationRouteWaypointName({
          customName: draft.customName,
          fullAddress: draft.fullAddress,
          fallbackName: t("stationRoute.waypoint.DEFAULT_NAME"),
        }),
        locationInfo: {
          latitude: draft.latitude,
          longitude: draft.longitude,
          fullAddress: draft.fullAddress,
        },
        waypointCategory: WaypointCategoryEnum.CourseWaypoint,
        areaId,
        timeArrival: neighborStation?.timeArrival ?? ZERO_TIME_HHmm,
      };

      const stationsUpdated = insertStationRouteWaypoint({
        stations: courseStations,
        waypoint,
        insertAfterIndex,
      });

      const metadataWithWaypoint: ICourseMetadata = {
        ...courseDto.metadata,
        stationsInfo: stationsUpdated,
        // Waypoints change the route — drop manual polyline and recount live.
        [CourseMetadataKeys.isManualPolylineEdit]: false,
        [CourseMetadataKeys.routePolyline]: undefined,
        ...(doNotAutoUpdateCourses
          ? {}
          : { distances: undefined, durations: undefined }),
      };

      applyEditingCourseMetadataLocally({
        metadata: metadataWithWaypoint,
        skipReorderStationsByTime: true,
      });
      setValue(CourseFormFields.isManualPolylineEdit, false, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(CourseFormFields.routePolyline, undefined, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setCourseHasUnsavedStationChange(true);

      return updateEditingCourseMetadata({
        metadata: metadataWithWaypoint,
        optimize: false,
        skipApiCheck: false,
        rebuildType: RebuildRouteTypeEnum.KeepOrder,
        skipReorderStationsByTime: true,
      });
    },
  );

  return {
    addWaypointFromDraft,
    courseDto,
    stations,
  };
};
